import { findPlanById } from '@/data/services/plan/plan-service-utils'
import { ValidAccountState } from '@/presentation/contexts/auth'
import { generateUserNameSummarized } from '@/presentation/helpers/account-profile-helper'
import useAccount from '@/presentation/hooks/auth/use-account'
import { useAvailablePlans } from '@/presentation/hooks/plan/use-available-plans'
import '@assets/styles/ellipsis-text.css'
import { Menu, MenuItem, Typography, useTheme } from '@mui/material'
import PlanChip from '../../../chip/plan-chip'

import { MenuList } from '../shared/menu-section-list'

type DesktopUserMenuProps = {
  anchorEl: HTMLElement | null
  handleClose: () => void
}

export default function DesktopUserMenu({ anchorEl, handleClose }: DesktopUserMenuProps) {
  const theme = useTheme()
  const [account] = useAccount() as ValidAccountState
  const { data: availablePlans = [] } = useAvailablePlans()
  const plan = account?.passPlan
    ? findPlanById(availablePlans, account.passPlan.id as number)
    : undefined

  const menuSlotProps = {
    paper: {
      elevation: 0,
      sx: {
        borderRadius: theme.spacing(1),
        minWidth: theme.spacing(40),
        overflow: 'visible',
        overflowY: 'auto',
        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
        '& .MuiAvatar-root': {
          width: 32,
          height: 32,
          ml: -0.5,
          mr: 1
        },
        '&::before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 0,
          right: 14,
          width: 10,
          height: 10,
          bgcolor: 'background.paper',
          transform: 'translateY(-50%) rotate(45deg)',
          zIndex: 0
        },

        '& .MuiTypography-root': {
          fontSize: theme.typography.pxToRem(16)
        },

        '& .MuiTypography-caption': {
          fontSize: theme.typography.pxToRem(12)
        },

        '& .MuiListItemButton-root': {
          margin: 0
        },

        '& .MuiList-root': {
          display: 'flex',
          flexDirection: 'column',

          '& .MuiMenuItem-root': {
            padding: ' 10px 16px',

            '& .MuiSvgIcon-root': {
              marginRight: '12px'
            }
          },

          '& .MuiChip-root': {
            '& .MuiSvgIcon-root': {
              marginRight: '0'
            }
          }
        }
      }
    }
  }

  return (
    <Menu
      id="menu-appbar"
      anchorEl={anchorEl}
      keepMounted
      slotProps={menuSlotProps}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      sx={{ borderRadius: 10, display: { xs: 'none', md: 'inherit' } }}
      disableScrollLock
    >
      <MenuItem
        sx={{
          pointerEvents: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(1)
        }}
      >
        <Typography variant="body1" color="grey.700" className="ellipsis-1-line capitalize">
          {generateUserNameSummarized(account.displayName)}
        </Typography>
        <Typography variant="body2" color="grey.500" className="ellipsis-1-line">
          {account.email}
        </Typography>
        <PlanChip plan={plan} />
      </MenuItem>

      <MenuList account={account} variant="desktop" handleClose={handleClose} />
    </Menu>
  )
}
